/**
 * Session Manager - Auto logout on inactivity
 * FREE - No external services required
 */

export class SessionManager {
  private static timeout: NodeJS.Timeout | null = null;
  private static readonly TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
  private static readonly WARNING_MS = 10 * 60 * 1000; // 10 minutes before timeout
  private static onTimeoutCallback: (() => void) | null = null;
  private static onWarningCallback: (() => void) | null = null;
  private static warningTimeout: NodeJS.Timeout | null = null;
  
  /**
   * Start session monitoring
   */
  static startSession(
    onTimeout: () => void,
    onWarning?: () => void
  ) {
    this.onTimeoutCallback = onTimeout;
    this.onWarningCallback = onWarning || null;
    
    this.resetTimeout();
    
    // Reset on user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, this.handleActivity);
    });
    
    console.log('✅ Session monitoring started (1 hour timeout)');
  }
  
  /**
   * Handle user activity
   */
  private static handleActivity = () => {
    this.resetTimeout();
  };
  
  /**
   * Reset timeout
   */
  private static resetTimeout() {
    // Clear existing timeouts
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
    
    // Set warning timeout
    if (this.onWarningCallback) {
      this.warningTimeout = setTimeout(() => {
        console.warn('⚠️ Session will expire in 10 minutes');
        this.onWarningCallback?.();
      }, this.TIMEOUT_MS - this.WARNING_MS);
    }
    
    // Set main timeout
    this.timeout = setTimeout(() => {
      console.log('🔒 Session timeout - logging out');
      this.onTimeoutCallback?.();
    }, this.TIMEOUT_MS);
    
    // Update last activity
    localStorage.setItem('last_activity', Date.now().toString());
  }
  
  /**
   * Stop session monitoring
   */
  static stopSession() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
    
    // Remove event listeners
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.handleActivity);
    });
    
    localStorage.removeItem('last_activity');
    console.log('🔓 Session monitoring stopped');
  }
  
  /**
   * Get time until timeout
   */
  static getTimeUntilTimeout(): number {
    const lastActivity = localStorage.getItem('last_activity');
    
    if (!lastActivity) {
      return this.TIMEOUT_MS;
    }
    
    const elapsed = Date.now() - parseInt(lastActivity);
    return Math.max(0, this.TIMEOUT_MS - elapsed);
  }
  
  /**
   * Manually extend session
   */
  static extendSession() {
    this.resetTimeout();
    console.log('✅ Session extended');
  }
  
  /**
   * Check if session is active
   */
  static isSessionActive(): boolean {
    return this.timeout !== null;
  }
}

